import { initializeAnalytics } from '@/shared/lib/analytics';
import { initializeFaroAnalytics } from '@/shared/lib/faroAnalytics';
import {
  identifyPosthogUser,
  initPosthog,
  setPosthogOrganization,
} from '@/shared/lib/posthogAnalytics';

import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import { reportsModel } from '@/entities/reports';
import { userModel } from '@/entities/user';

import { initializeFaroView } from './faroView';
import { store } from './store';

// PostHog creates person profiles only for identified users; identifying the
// current user (once the user slice has it) also merges the anonymous
// pre-login events into that person.
let identifiedPosthogUserId: string | undefined;

const identifyPosthogWhenUserKnown = (): void => {
  const currentUser = userModel.selectors.selectCurrentUser(store.getState());
  const userID = currentUser?.ID;

  if (userID && userID !== identifiedPosthogUserId) {
    identifiedPosthogUserId = userID;
    identifyPosthogUser(userID, currentUser?.PersonalData?.Emails?.[0] || '');
  }
};

// Registers the current organization as a PostHog group (org-level funnels
// and retention); re-registers when the organization changes.
let groupedPosthogOrganizationId: string | undefined;

const groupPosthogWhenOrganizationKnown = (): void => {
  const state = store.getState();
  const organizationID =
    organizationModel.selectors.selectCurrentOrganizationID(state);

  if (organizationID && organizationID !== groupedPosthogOrganizationId) {
    groupedPosthogOrganizationId = organizationID;
    const organizationName =
      organizationModel.selectors.selectCurrentOrganization(state)?.Name || '';
    setPosthogOrganization(
      organizationID,
      organizationName ? { name: organizationName } : {},
    );
  }
};

// HACK: We need this data in analytics to avoid passing it every time in the event tracker. We are implementing it here because connecting the store directly in analytics causes a circular dependency, and the application crashes
export const setupAnalytics = () => {
  // Initialize Faro Web SDK first
  initializeFaroAnalytics();
  initializeFaroView();

  // Initialize PostHog (no-op unless the PostHog env vars are set)
  initPosthog();
  store.subscribe(identifyPosthogWhenUserKnown);
  store.subscribe(groupPosthogWhenOrganizationKnown);

  initializeAnalytics(() => {
    const state = store.getState();
    const currentUser = userModel.selectors.selectCurrentUser(state);

    // NOTE: these are default values, but keep in mind that some can be undefined at times. In that case, you must pass these values during the eventTrack call
    return {
      patientID: patientModel.selectors.selectCurrentPatientID(state),
      reportID: reportsModel.selectors.selectCurrentReportID(state),
      reportType: reportsModel.selectors.selectCurrentReportType(state),
      userID: currentUser.ID,
      email: currentUser?.PersonalData?.Emails?.[0] || '',
      organizationID:
        organizationModel.selectors.selectCurrentOrganizationID(state),
    };
  });
};
